import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  FormulaRefusalError,
  getShowcaseFormula,
  previewShowcaseFormula,
  putShowcaseFormula,
} from '@/lib/api/client/ymarket-pricing'
import type {
  FormulaDefinition,
  FormulaResults,
  FormulaValue,
  FormulaVersion,
  YmShowcaseFormula,
  YmShowcaseFormulaPreviewRow,
} from '@/lib/api/schemas/ymarket-pricing.schema'
import { formatRubleAmount, formatRublePerGram } from '@/lib/market-money'
import { productTypeLabel, unavailableReasonText } from '@/lib/warehouse/labels'

const GROUP = 'ymarket_showcase'
const AUTOPRICE_GROUP = 'autoprice'
const TAX_KEY = 'tax'
const MAX_EXTRA_OFFERS = 20

interface Refusal {
  key: string | null
  position: number | null
  message: string
}

function when(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Europe/Moscow' }).format(new Date(value))
}

function versionText(definition: FormulaDefinition): string {
  const version = definition.version
  if (version === null) return 'по умолчанию'
  const note = version.note === null ? '' : ` — ${version.note}`
  return `изменена ${when(version.setAt)}, ${version.actor}${note}`
}

function refusalOf(reason: unknown): Refusal {
  if (reason instanceof FormulaRefusalError) return { key: reason.key, position: reason.position, message: reason.message }
  return { key: null, position: null, message: reason instanceof Error && reason.message.length > 0 ? reason.message : 'Не удалось выполнить запрос.' }
}

function excerpt(source: string, position: number): string {
  const index = Math.max(0, Math.min(source.length, position - 1))
  return `${source.slice(Math.max(0, index - 20), index)}⟶${source.slice(index, index + 20)}`
}

function valueText(value: FormulaValue | undefined, perGram = false): string {
  if (value === undefined) return '—'
  if (value.value === null) return '—'
  return perGram ? formatRublePerGram(value.value) : formatRubleAmount(value.value)
}

function valueTitle(value: FormulaValue | undefined): string | undefined {
  if (value === undefined || value.value !== null) return undefined
  return value.message ?? (value.reason === null ? undefined : unavailableReasonText(value.reason))
}

function perGramValue(results: FormulaResults | null): FormulaValue | undefined {
  if (results === null) return undefined
  return { value: results.inHandPerGram, reason: results.inHand.reason, message: results.inHand.message }
}

function Comparison({ current, candidate, perGram = false }: { current: FormulaValue | undefined; candidate: FormulaValue | undefined; perGram?: boolean }) {
  const changed = current?.value !== candidate?.value
  return (
    <td className="px-2 py-1.5 tabular-nums">
      <span title={valueTitle(current)}>{valueText(current, perGram)}</span>
      {changed ? (
        <span data-changed="true" className="ml-1 font-semibold text-ink" title={valueTitle(candidate)}>→ {valueText(candidate, perGram)}</span>
      ) : null}
    </td>
  )
}

function PreviewTable({ rows }: { rows: YmShowcaseFormulaPreviewRow[] }) {
  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full min-w-[720px] text-left text-xs" aria-label="Проверка формул">
        <thead className="text-ink-muted">
          <tr>
            <th className="px-2 py-1.5 font-medium">Изделие</th>
            <th className="px-2 py-1.5 font-medium">Отчёт «Цены»</th>
            <th className="px-2 py-1.5 font-medium">Кабинет 29.09</th>
            <th className="px-2 py-1.5 font-medium">Витрина</th>
            <th className="px-2 py-1.5 font-medium">С Пэй</th>
            <th className="px-2 py-1.5 font-medium">На руки, ₽/г</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.offerId} data-offer={row.offerId} className="border-t border-rule align-top">
              <td className="px-2 py-1.5">
                <span className="block text-ink">{row.label ?? row.offerId}</span>
                <span className="block text-ink-muted">
                  {row.oracle ? 'эталон кабинета · ' : ''}
                  {row.category === null ? 'тип не указан' : productTypeLabel(row.category)}
                  {row.weight === null ? '' : ` · ${row.weight.toLocaleString('ru-RU')} г`}
                </span>
              </td>
              {row.current === null ? (
                <td colSpan={5} className="px-2 py-1.5 text-ink-muted">{unavailableReasonText(row.unavailableReason ?? 'price_not_collected')}</td>
              ) : (
                <>
                  <td className="px-2 py-1.5 tabular-nums">
                    {row.onDisplay === null ? '—' : formatRubleAmount(row.onDisplay)}
                    {row.onDisplayPay === null ? '' : ` · с Пэй ${formatRubleAmount(row.onDisplayPay)}`}
                  </td>
                  <td className="px-2 py-1.5 tabular-nums">
                    {row.cabinetNoPay === null ? '—' : `${formatRubleAmount(row.cabinetNoPay)} · с Пэй ${formatRubleAmount(row.cabinetPay ?? row.cabinetNoPay)}`}
                  </td>
                  <Comparison current={row.current.showcase} candidate={row.candidate?.showcase} />
                  <Comparison current={row.current.showcasePay} candidate={row.candidate?.showcasePay} />
                  <Comparison current={perGramValue(row.current)} candidate={perGramValue(row.candidate)} perGram />
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Reference({ formula }: { formula: YmShowcaseFormula }) {
  const variables = [...new Map(formula.formulas.filter((definition) => definition.group === GROUP || definition.group === AUTOPRICE_GROUP || definition.key === TAX_KEY).flatMap((definition) => definition.variables).map((variable) => [variable.name, variable])).values()]
  return (
    <details className="mt-4 text-xs text-ink-2">
      <summary className="cursor-pointer text-ink-muted">Переменные и функции</summary>
      <p className="mt-2 text-ink-muted">
        Числа пишутся с точкой: 0.9315. Операции: + − * /, сравнения == != &lt; &lt;= &gt; &gt;=, and, or, not, скобки.
        Неизвестное значение делает результат неизвестным; coalesce берёт первое известное.
        До {formula.limits.maxLength} символов, вложенность до {formula.limits.maxDepth}, до {formula.limits.maxNodes} элементов.
      </p>
      <table className="mt-2 w-full text-left" aria-label="Переменные формул">
        <tbody>
          {variables.map((variable) => (
            <tr key={variable.name} className="border-t border-rule align-top">
              <td className="py-1 pr-3 font-mono text-ink">{variable.name}</td>
              <td className="py-1 pr-3">{variable.label}</td>
              <td className="py-1 text-ink-muted">
                {variable.description}
                {variable.values === null ? '' : ` Значения: ${variable.values.join(', ')}.`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <ul className="mt-2 space-y-0.5" aria-label="Функции формул">
        {formula.functions.map((item) => (
          <li key={item.name}><span className="font-mono text-ink">{item.signature}</span> — {item.description}</li>
        ))}
      </ul>
    </details>
  )
}

/** «Цена на витрине Маркета»: the owner's showcase, Yandex Pay and in-hand formulas with preview and history. */
export function ShowcaseFormulaSettings() {
  const [formula, setFormula] = useState<YmShowcaseFormula | null>(null)
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [note, setNote] = useState('')
  const [offers, setOffers] = useState('')
  const [preview, setPreview] = useState<YmShowcaseFormulaPreviewRow[] | null>(null)
  const [refusal, setRefusal] = useState<Refusal | null>(null)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)

  const adopt = useCallback((value: YmShowcaseFormula) => {
    setFormula(value)
    setDrafts(Object.fromEntries(value.formulas.map((definition) => [definition.key, definition.source])))
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    getShowcaseFormula(controller.signal)
      .then((value) => { adopt(value); setRefusal(null) })
      .catch((reason: unknown) => { if (!controller.signal.aborted) setRefusal(refusalOf(reason)) })
    return () => controller.abort()
  }, [adopt])

  const definitions = useMemo(
    () => formula?.formulas.filter((definition) => definition.group === GROUP || definition.group === AUTOPRICE_GROUP || definition.key === TAX_KEY) ?? [],
    [formula],
  )
  const changes = useMemo(
    () => Object.fromEntries(definitions.filter((definition) => (drafts[definition.key] ?? definition.source).trim() !== definition.source).map((definition) => [definition.key, (drafts[definition.key] ?? '').trim()])),
    [definitions, drafts],
  )
  const changed = Object.keys(changes).length > 0
  const offerIds = offers.split(/[\s,]+/).map((value) => value.trim()).filter((value) => value.length > 0).slice(0, MAX_EXTRA_OFFERS)

  const check = async () => {
    setBusy(true)
    setSaved(false)
    try {
      setPreview((await previewShowcaseFormula({ formulas: changes, offerIds })).rows)
      setRefusal(null)
    } catch (reason: unknown) {
      setRefusal(refusalOf(reason))
    } finally {
      setBusy(false)
    }
  }

  const save = async (body: Record<string, string>, text: string) => {
    setBusy(true)
    try {
      adopt(await putShowcaseFormula(text.trim() === '' ? { formulas: body } : { formulas: body, note: text.trim() }))
      setRefusal(null)
      setPreview(null)
      setNote('')
      setSaved(true)
    } catch (reason: unknown) {
      setRefusal(refusalOf(reason))
      setSaved(false)
    } finally {
      setBusy(false)
    }
  }

  const revert = (version: FormulaVersion) => {
    void save({ [version.key]: version.source }, `возврат к версии от ${when(version.setAt)}`)
  }

  const proposal = formula?.proposal ?? null
  const labelOf = (key: string) => definitions.find((definition) => definition.key === key)?.label ?? key
  return (
    <section className="rounded-lg border border-rule bg-paper p-4 sm:p-5" aria-label="Цена на витрине Маркета">
      <h2 className="text-[15px] font-semibold text-ink">Цена на витрине Маркета</h2>
      <p className="mt-1 text-xs text-ink-muted">
        Витрина, цена с Яндекс Пэй, сумма на руки, порог дешёвой витрины, налог и авто-переоценка считаются по формулам ниже. Сохранённые формулы действуют со следующей загрузки страницы: карточка, склад, фильтры, вид «Маркет: ниже порога», переоценка и юнит-экономика.
      </p>
      {refusal !== null && refusal.key === null ? <p role="alert" className="mt-3 text-xs text-st-unpaid">{refusal.message}</p> : null}
      {saved ? <p aria-live="polite" className="mt-3 text-xs text-ink-2">Формулы сохранены.</p> : null}
      {formula === null ? (
        refusal === null ? <p aria-live="polite" className="mt-3 text-xs text-ink-muted">Загружаем формулы…</p> : null
      ) : (
        <>
          <div className="mt-4 space-y-4">
            {definitions.map((definition) => {
              const draft = drafts[definition.key] ?? definition.source
              const own = refusal !== null && refusal.key === definition.key ? refusal : null
              return (
                <div key={definition.key} data-formula={definition.key}>
                  <label className="block text-xs font-semibold text-ink-2" htmlFor={`formula-${definition.key}`}>
                    {definition.label} <span className="font-mono font-normal text-ink-muted">{definition.key}</span>
                  </label>
                  <p className="text-xs text-ink-muted">{definition.description}</p>
                  <textarea
                    id={`formula-${definition.key}`}
                    value={draft}
                    onChange={(event) => { setDrafts((current) => ({ ...current, [definition.key]: event.target.value })); setSaved(false) }}
                    maxLength={formula.limits.maxLength}
                    rows={2}
                    spellCheck={false}
                    aria-invalid={own !== null}
                    className="mt-1 w-full rounded-md border border-rule bg-paper px-2.5 py-1.5 font-mono text-sm"
                  />
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink-muted">
                    <span>{versionText(definition)}</span>
                    {draft.trim() !== definition.default ? (
                      <button type="button" className="underline" onClick={() => setDrafts((current) => ({ ...current, [definition.key]: definition.default }))}>
                        Подставить формулу по умолчанию
                      </button>
                    ) : null}
                  </div>
                  {definition.storedError !== null ? (
                    <p className="mt-1 text-xs text-st-unpaid">Сохранённая формула не проходит проверку ({definition.storedError}); значения недоступны, пока её не исправить.</p>
                  ) : null}
                  {own !== null ? (
                    <p role="alert" className="mt-1 text-xs text-st-unpaid">
                      {own.position === null ? own.message : `Символ ${own.position}: ${own.message}`}
                      {own.position === null ? null : <span className="mt-0.5 block font-mono text-ink-2">{excerpt(draft, own.position)}</span>}
                    </p>
                  ) : null}
                </div>
              )
            })}
          </div>
          {proposal !== null ? (
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-gold/50 bg-gold/10 px-3 py-2 text-xs text-ink-2" data-formula="proposal">
              <span>По заказам: {proposal.value.toLocaleString('ru-RU', { maximumFractionDigits: 4 })} ({proposal.sampleCount ?? 0} заказов, {when(proposal.setAt)})</span>
              <button
                type="button"
                onClick={() => setDrafts((current) => ({ ...current, showcase: `round(onDisplay * ${proposal.value})` }))}
                className="rounded-md border border-rule bg-paper px-2.5 py-1 font-medium text-ink hover:bg-bone"
              >Подставить в формулу витрины</button>
            </div>
          ) : null}
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <label className="text-xs font-semibold text-ink-2">
              Ещё предложения для проверки (offerId через запятую)
              <input value={offers} onChange={(event) => setOffers(event.target.value)} className="mt-1 w-full rounded-md border border-rule bg-paper px-2.5 py-1.5 text-sm" />
            </label>
            <label className="text-xs font-semibold text-ink-2">
              Комментарий
              <input value={note} onChange={(event) => setNote(event.target.value)} maxLength={500} placeholder="цепи по кабинету 29.09" className="mt-1 w-full rounded-md border border-rule bg-paper px-2.5 py-1.5 text-sm" />
            </label>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" disabled={busy} onClick={() => { void check() }} className="rounded-md border border-rule bg-paper px-3 py-1.5 text-sm font-medium text-ink hover:bg-bone disabled:opacity-50">
              {busy ? 'Считаем…' : 'Проверить'}
            </button>
            <button type="button" disabled={busy || !changed} onClick={() => { void save(changes, note) }} className="rounded-md bg-onyx px-3 py-1.5 text-sm font-medium text-paper disabled:opacity-50">
              Сохранить
            </button>
          </div>
          {preview !== null ? <PreviewTable rows={preview} /> : null}
          <Reference formula={formula} />
          {formula.history.length > 0 ? (
            <details className="mt-4 text-xs text-ink-2" open>
              <summary className="cursor-pointer text-ink-muted">История</summary>
              <ul className="mt-2 space-y-1" aria-label="История формул">
                {formula.history.map((version) => {
                  const current = definitions.find((definition) => definition.key === version.key)?.source === version.source
                  return (
                    <li key={version.id} className="flex flex-wrap items-center gap-2">
                      <span>{when(version.setAt)} · {labelOf(version.key)}: <span className="font-mono">{version.source}</span> · {version.actor}{version.note === null ? '' : ` — ${version.note}`}</span>
                      {current ? <span className="text-ink-muted">действует</span> : (
                        <button type="button" disabled={busy} onClick={() => revert(version)} className="rounded-md border border-rule bg-paper px-2 py-0.5 font-medium text-ink hover:bg-bone disabled:opacity-50">Вернуть</button>
                      )}
                    </li>
                  )
                })}
              </ul>
            </details>
          ) : null}
        </>
      )}
    </section>
  )
}
